import { useMemo, useCallback, useState } from 'react';
import {
  ReactFlow, Background, Handle, Position, BaseEdge, EdgeLabelRenderer,
  useInternalNode, getStraightPath, MarkerType,
  type Node, type Edge, type NodeProps, type EdgeProps, type InternalNode, type ReactFlowInstance,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Play, Search } from 'lucide-react';
import type { Technique, StatusMap, Category } from '../types';
import { TechniqueStatus } from '../types';
import { categories } from '../data/categories';
import { CATEGORY_ICONS, TYPE_ICONS, type IconComponent } from '../data/icons';
import { positionLinks } from '../data/positionLinks';

// ─── Node renderers ───
interface PositionData { label: string; color: string; count: number; catId: string; isFocus?: boolean; drillable?: boolean; Icon?: IconComponent; [k: string]: unknown }
interface TechniqueData { label: string; color: string; done: boolean; playable: boolean; tech: Technique; [k: string]: unknown }

function PositionNode({ data }: NodeProps) {
  const d = data as PositionData;
  const Icon = d.Icon;
  return (
    <div className={`mm-pos${d.isFocus ? ' mm-pos--focus' : ''}`} style={{ background: d.color }}>
      <Handle type="target" position={Position.Bottom} className="mm-handle" isConnectable={false} />
      {Icon && <Icon size={d.isFocus ? 22 : 18} strokeWidth={2.25} />}
      <span className="mm-pos-label">{d.label}</span>
      {d.count > 0 && <span className="mm-pos-count">{d.count}</span>}
      {d.drillable && <Search className="mm-pos-drill" size={d.isFocus ? 16 : 14} strokeWidth={2.5} aria-hidden="true" />}
      <Handle type="source" position={Position.Top} className="mm-handle" isConnectable={false} />
    </div>
  );
}

function TechniqueNode({ data }: NodeProps) {
  const d = data as TechniqueData;
  // Reuse the Library card's look (tier bar + play + type icon + name) so a
  // technique reads the same everywhere; click-to-play is handled on node click.
  const TypeIcon = d.tech?.type ? TYPE_ICONS[d.tech.type] : undefined;
  return (
    <div className={`technique-card mm-tech-card${d.done ? ' mm-tech-card--done' : ''}${d.playable ? '' : ' mm-tech-card--disabled'}`}>
      <Handle type="target" position={Position.Bottom} className="mm-handle" isConnectable={false} />
      <span className="technique-tier-bar" style={{ backgroundColor: d.color }} />
      {d.playable && (
        <span className="technique-play" aria-hidden="true"><Play size={14} fill="currentColor" /></span>
      )}
      {TypeIcon && <TypeIcon size={15} className="technique-type-icon" />}
      <span className="technique-label"><span className="technique-name-row">{d.label}</span></span>
      <Handle type="source" position={Position.Top} className="mm-handle" isConnectable={false} />
    </div>
  );
}

const nodeTypes = { position: PositionNode, technique: TechniqueNode };

// ─── Floating edge with a midpoint label (radial ego-graph spokes) ───
function boundaryPoint(node: InternalNode, towards: { x: number; y: number }) {
  const w = (node.measured?.width ?? 0) / 2;
  const h = (node.measured?.height ?? 0) / 2;
  const cx = node.internals.positionAbsolute.x + w;
  const cy = node.internals.positionAbsolute.y + h;
  const dx = towards.x - cx;
  const dy = towards.y - cy;
  if (dx === 0 && dy === 0) return { x: cx, y: cy };
  const scale = 1 / Math.max(Math.abs(dx) / (w || 1), Math.abs(dy) / (h || 1));
  return { x: cx + dx * scale, y: cy + dy * scale };
}
function centerOf(node: InternalNode) {
  return {
    x: node.internals.positionAbsolute.x + (node.measured?.width ?? 0) / 2,
    y: node.internals.positionAbsolute.y + (node.measured?.height ?? 0) / 2,
  };
}
function FloatingEdge({ source, target, label, style, markerEnd }: EdgeProps) {
  const sourceNode = useInternalNode(source);
  const targetNode = useInternalNode(target);
  if (!sourceNode || !targetNode) return null;
  const s = boundaryPoint(sourceNode, centerOf(targetNode));
  const t = boundaryPoint(targetNode, centerOf(sourceNode));
  const [path, labelX, labelY] = getStraightPath({ sourceX: s.x, sourceY: s.y, targetX: t.x, targetY: t.y });
  return (
    <>
      <BaseEdge path={path} style={style} markerEnd={markerEnd} />
      {label && (
        <EdgeLabelRenderer>
          <div className="mm-edge-label" style={{ transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)` }}>
            {label}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}
const edgeTypes = { floating: FloatingEdge };

// ─── Layout ───
interface LayoutNode {
  id: string;
  kind: 'guard' | 'tech';
  x: number;          // node centre
  y: number;          // node centre (negative = higher up)
  halfW: number;
  halfH: number;
  color?: string;
  label?: string;
  count?: number;
  catId?: string;
  tech?: Technique;
  isFocus?: boolean;
}

function estWidth(label: string, base: number, per: number, max: number) {
  return Math.min(max, label.length * per + base);
}

const EGO_RADIUS = 330;   // distance from the focused position to its neighbours
// Technique bloom — concentric rings around the focused position.
const RING_FIRST = 270;   // radius of the innermost ring
const RING_GAP = 150;     // radius added per outer ring
const RING_MIN_ARC = 300; // min centre-to-centre arc spacing between card nodes
const CLUSTER_W = 1500;   // horizontal gap between blooms (multi-select)

const catById = new Map(categories.map((c) => [c.id, c] as const));

interface Guard { c: Category; techs: Technique[] }
function group(techniques: Technique[]): Map<string, Guard> {
  const byCat = new Map<string, Technique[]>();
  for (const t of techniques) {
    const list = byCat.get(t.categoryId);
    if (list) list.push(t);
    else byCat.set(t.categoryId, [t]);
  }
  const present = new Map<string, Guard>();
  for (const c of categories) {
    const techs = byCat.get(c.id);
    if (techs) present.set(c.id, { c, techs: techs.slice().sort((a, z) => a.name.localeCompare(z.name)) });
  }
  return present;
}

interface Neighbor { catId: string; label: string; outgoing: boolean; bidirectional: boolean }
// Directly-linked positions for a focus, de-duped, keeping only real categories.
function neighborsOf(focus: string): Neighbor[] {
  const seen = new Set<string>();
  const out: Neighbor[] = [];
  for (const l of positionLinks) {
    const other = l.from === focus ? l.to : l.to === focus ? l.from : null;
    if (!other || other === focus || !catById.has(other) || seen.has(other)) continue;
    seen.add(other);
    out.push({ catId: other, label: l.label, outgoing: l.from === focus, bidirectional: !!l.bidirectional });
  }
  return out;
}

// Ego-graph: the focused position centred, its linked positions on a ring around
// it with labelled transition edges. Tapping a neighbour re-centres the graph.
function egoLayout(focus: string, present: Map<string, Guard>): { placed: LayoutNode[]; edges: Edge[] } {
  const fc = catById.get(focus);
  if (!fc) return { placed: [], edges: [] };

  const nodes: LayoutNode[] = [{
    id: `g-${fc.id}`, kind: 'guard', x: 0, y: 0, halfW: 100, halfH: 30,
    color: fc.color, label: fc.label, count: present.get(fc.id)?.techs.length ?? 0, catId: fc.id, isFocus: true,
  }];
  const edges: Edge[] = [];

  const neighbours = neighborsOf(focus);
  const n = neighbours.length || 1;
  neighbours.forEach((nb, i) => {
    const cat = catById.get(nb.catId)!;
    const angle = -Math.PI / 2 + (i * 2 * Math.PI) / n;
    const gw = estWidth(cat.label, 72, 8.6, 220);
    nodes.push({
      id: `g-${cat.id}`, kind: 'guard', x: Math.cos(angle) * EGO_RADIUS, y: Math.sin(angle) * EGO_RADIUS,
      halfW: gw / 2, halfH: 24, color: cat.color, label: cat.label, count: present.get(cat.id)?.techs.length ?? 0, catId: cat.id,
    });
    edges.push({
      id: `e-${focus}-${cat.id}`, type: 'floating',
      // Arrow follows the link direction; bidirectional flows get no arrowhead.
      source: nb.outgoing ? `g-${focus}` : `g-${cat.id}`,
      target: nb.outgoing ? `g-${cat.id}` : `g-${focus}`,
      label: nb.label,
      markerEnd: nb.bidirectional ? undefined : { type: MarkerType.ArrowClosed, color: '#8a8aa8', width: 18, height: 18 },
      style: { stroke: '#6a6a8a', strokeWidth: 2, opacity: 0.75 },
    });
  });
  return { placed: nodes, edges };
}

// Slots for n nodes packed onto concentric rings, filling each ring before
// stepping outward; alternate rings are angle-staggered so spokes don't line up.
function ringSlots(n: number): { x: number; y: number }[] {
  const slots: { x: number; y: number }[] = [];
  let placed = 0;
  let ring = 1;
  while (placed < n) {
    const radius = RING_FIRST + (ring - 1) * RING_GAP;
    const cap = Math.max(5, Math.floor((2 * Math.PI * radius) / RING_MIN_ARC));
    const take = Math.min(cap, n - placed);
    const stagger = (ring % 2) * 0.5;
    for (let i = 0; i < take; i++) {
      const a = -Math.PI / 2 + ((i + stagger) * 2 * Math.PI) / take;
      slots.push({ x: Math.cos(a) * radius, y: Math.sin(a) * radius });
    }
    placed += take;
    ring++;
  }
  return slots;
}

// Techniques view: each selected position's techniques bloom radially around it
// (concentric rings + spokes), matching the ego-graph's look.
function bloomLayout(present: Map<string, Guard>): { placed: LayoutNode[]; edges: Edge[] } {
  const nodes: LayoutNode[] = [];
  const edges: Edge[] = [];
  [...present.values()].forEach(({ c, techs }, gi) => {
    const cx = gi * CLUSTER_W;
    nodes.push({
      id: `g-${c.id}`, kind: 'guard', x: cx, y: 0, halfW: 100, halfH: 30,
      color: c.color, label: c.label, count: techs.length, catId: c.id, isFocus: true,
    });
    const slots = ringSlots(techs.length);
    techs.forEach((t, ti) => {
      const s = slots[ti];
      const tw = estWidth(t.name, 95, 6.6, 250); // card chrome (tier bar + play + type icon) ≈ 95px
      nodes.push({
        id: t.id, kind: 'tech', x: cx + s.x, y: s.y, halfW: tw / 2, halfH: 20,
        color: c.color, label: t.name, tech: t,
      });
      edges.push({ id: `e-${c.id}-${t.id}`, type: 'floating', source: `g-${c.id}`, target: t.id,
        style: { stroke: c.color, strokeWidth: 1.6, opacity: 0.32 } });
    });
  });
  return { placed: nodes, edges };
}

function computeLayout(techniques: Technique[], focus: string, mode: 'ego' | 'techniques'): { placed: LayoutNode[]; edges: Edge[] } {
  const present = group(techniques);
  if (present.size === 0) return { placed: [], edges: [] };
  return mode === 'techniques' ? bloomLayout(present) : egoLayout(focus, present);
}

const DEFAULT_FOCUS = catById.has('closed-guard') ? 'closed-guard' : (categories[0]?.id ?? '');

interface OntologyProps {
  /** Already filtered by the shared filter interface (same as the other pages). */
  techniques: Technique[];
  statusMap: StatusMap;
  onPlay: (tech: Technique) => void;
  /** A category selected here drills into its techniques (drives the shared filter). */
  onFilterCategory: (catId: string) => void;
  /** Active category filter — a selection switches Paths into the techniques view. */
  categoryFilter: Set<string>;
}

export default function Ontology({ techniques, statusMap, onPlay, onFilterCategory, categoryFilter }: OntologyProps) {
  // The position the ego-graph is centred on. A category filter switches to the
  // techniques spine; clearing it returns here, still centred where you left off.
  const [focus, setFocus] = useState<string>(DEFAULT_FOCUS);
  const mode: 'ego' | 'techniques' = categoryFilter.size >= 1 ? 'techniques' : 'ego';

  const layout = useMemo(() => computeLayout(techniques, focus, mode), [techniques, focus, mode]);

  const nodes = useMemo<Node[]>(() => layout.placed.map((n) => {
    const position = { x: (n.x ?? 0) - n.halfW, y: (n.y ?? 0) - n.halfH };
    if (n.kind === 'guard') {
      return {
        id: n.id, type: 'position', position, draggable: false,
        data: { label: n.label, color: n.color, count: n.count, catId: n.catId!, isFocus: n.isFocus, drillable: mode === 'ego', Icon: n.catId ? CATEGORY_ICONS[n.catId] : undefined } as PositionData,
      };
    }
    return {
      id: n.id, type: 'technique', position, draggable: false,
      data: {
        label: n.label, color: n.color,
        done: statusMap[n.id] === TechniqueStatus.Completed,
        playable: !!n.tech?.instagramUrl, tech: n.tech,
      } as TechniqueData,
    };
  }), [layout, statusMap, mode]);

  const onNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
    if (node.type === 'position') {
      const d = node.data as PositionData;
      // In the ego-graph, the centre drills into techniques and a neighbour
      // re-centres; in the techniques view the position toggles back out.
      if (mode === 'ego' && !d.isFocus) setFocus(d.catId);
      else onFilterCategory(d.catId);
      return;
    }
    if (node.type !== 'technique') return;
    const d = node.data as TechniqueData;
    if (d.playable) onPlay(d.tech);
  }, [mode, onPlay, onFilterCategory]);

  const fitKey = `${mode}:${focus}:${layout.placed.length}`;

  const onInit = useCallback((inst: ReactFlowInstance) => {
    inst.fitView({ padding: 0.2 });
  }, []);

  if (layout.placed.length === 0) {
    return (
      <div className="ontology-body ontology-body--empty">
        <span className="ontology-empty">No techniques match these filters.</span>
      </div>
    );
  }

  return (
    <div className="ontology-body">
      <ReactFlow
        key={fitKey}
        nodes={nodes}
        edges={layout.edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodeClick={onNodeClick}
        onInit={onInit}
        minZoom={0.08}
        maxZoom={1.5}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        proOptions={{ hideAttribution: true }}
      >
        <Background color="#2a2a42" gap={24} size={1} />
      </ReactFlow>
    </div>
  );
}
